import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Loader2, Building, Home, MapPin, ArrowRight, Map } from 'lucide-react';
import { searchGlobal } from '../../../core/api/client';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';

interface NavbarSearchProps {
    isSolid: boolean;
    isRTL: boolean;
}

export const NavbarSearch = ({ isSolid, isRTL }: NavbarSearchProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Debounce query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(query);
        }, 300);
        return () => clearTimeout(timer);
    }, [query]);

    // Fetch suggestions
    const { data, isLoading } = useQuery({
        queryKey: ['search', 'global', debouncedQuery],
        queryFn: () => searchGlobal({ q: debouncedQuery }),
        enabled: debouncedQuery.length >= 2,
        staleTime: 60 * 1000,
    });

    const suggestions = data?.success ? data.data : { projects: [], units: [], areas: [] };
    const hasResults = suggestions.projects?.length > 0 || suggestions.units?.length > 0 || suggestions.areas?.length > 0;

    // Handle outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Open dropdown when typing or results arrive
    useEffect(() => {
        if (debouncedQuery.length >= 2 && (hasResults || isLoading)) {
            setIsOpen(true);
        } else if (debouncedQuery.length < 2) {
            setIsOpen(false);
        }
    }, [debouncedQuery, hasResults, isLoading]);

    const handleSearch = (e?: React.FormEvent) => {
        e?.preventDefault();
        if (query.trim()) {
            setIsOpen(false);
            navigate(`/search?q=${encodeURIComponent(query)}`);
            // Optional: Blur input
            (document.activeElement as HTMLElement)?.blur();
        }
    };

    const handleSelect = (path: string) => {
        setIsOpen(false);
        setQuery(''); // Clear search or keep it? Usually clear after nav.
        navigate(path);
    };

    return (
        <div className="relative group w-full" ref={containerRef}>
            <form onSubmit={handleSearch} className="relative">
                <button
                    type="submit"
                    className={clsx(
                        "absolute top-1/2 -translate-y-1/2 transition-colors flex items-center justify-center p-1",
                        isRTL ? "right-2" : "left-2",
                        isSolid ? "text-slate-400 hover:text-secondary" : "text-white/50 hover:text-white"
                    )}
                >
                    {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Search className="w-4 h-4" />
                    )}
                </button>
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => {
                        if (debouncedQuery.length >= 2) setIsOpen(true);
                    }}
                    placeholder={t('nav.search', 'Search...')}
                    className={clsx(
                        "w-full transition-all duration-300 rounded-full py-2.5 text-xs sm:text-sm focus:outline-none px-10",
                        isSolid
                            ? "bg-slate-100 border border-slate-200 text-secondary placeholder-slate-400 focus:border-secondary/50 focus:bg-white"
                            : "bg-white/10 border border-white/10 text-white placeholder-white/50 focus:bg-white/20 focus:border-white/30"
                    )}
                />
                <button
                    type="button"
                    onClick={() => navigate('/search?map=true')}
                    className={clsx(
                        "absolute top-1/2 -translate-y-1/2 transition-colors flex items-center justify-center p-1.5 rounded-full",
                        isRTL ? "left-1.5" : "right-1.5",
                        isSolid
                            ? "bg-white text-secondary hover:bg-gray-100 shadow-sm border border-gray-100"
                            : "bg-white/20 text-white hover:bg-white/30"
                    )}
                    title={t('nav.mapView', 'Map View')}
                >
                    <Map className="w-4 h-4" />
                </button>
            </form>

            {/* Suggestions Dropdown */}
            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                    {isLoading ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 opacity-50" />
                            {t('search.loading', 'Searching...')}
                        </div>
                    ) : hasResults ? (
                        <div className="max-h-[70vh] overflow-y-auto py-2">
                            {/* Areas */}
                            {suggestions.areas?.length > 0 && (
                                <div className="mb-2">
                                    <div className="px-4 py-1.5 text-xs font-bold text-gray-400 uppercase tracking-wider">
                                        {t('search.categories.areas', 'Areas')}
                                    </div>
                                    {suggestions.areas.map((area: any) => (
                                        <button
                                            key={area.id}
                                            onClick={() => handleSelect(`/search?location=${area.name}`)} // Areas usually filter search
                                            className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-3 transition-colors group"
                                        >
                                            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                                                <MapPin className="w-4 h-4" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-gray-900 truncate group-hover:text-secondary transition-colors">
                                                    {area.name}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Projects */}
                            {suggestions.projects?.length > 0 && (
                                <div className="mb-2">
                                    <div className="px-4 py-1.5 text-xs font-bold text-gray-400 uppercase tracking-wider">
                                        {t('search.categories.projects', 'Projects')}
                                    </div>
                                    {suggestions.projects.map((project: any) => (
                                        <button
                                            key={project.id}
                                            onClick={() => handleSelect(`/projects/${project.slug}`)}
                                            className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-3 transition-colors group"
                                        >
                                            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                                                <Building className="w-4 h-4" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-gray-900 truncate group-hover:text-secondary transition-colors">
                                                    {project.title}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Units */}
                            {suggestions.units?.length > 0 && (
                                <div className="mb-2">
                                    <div className="px-4 py-1.5 text-xs font-bold text-gray-400 uppercase tracking-wider">
                                        {t('search.categories.units', 'Units')}
                                    </div>
                                    {suggestions.units.map((unit: any) => (
                                        <button
                                            key={unit.id}
                                            onClick={() => handleSelect(`/units/${unit.slug}`)}
                                            className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-3 transition-colors group"
                                        >
                                            <div className="w-8 h-8 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
                                                <Home className="w-4 h-4" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-gray-900 truncate group-hover:text-secondary transition-colors">
                                                    {unit.title}
                                                </div>
                                                <div className="text-xs text-gray-500 truncate">
                                                    {unit.location} • {parseInt(unit.price).toLocaleString()} EGP
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* View All Link */}
                            <div className="border-t border-gray-100 mt-2 pt-2 px-2">
                                <button
                                    onClick={() => handleSearch()}
                                    className="w-full py-2.5 px-3 rounded-lg bg-secondary/5 text-secondary hover:bg-secondary/10 flex items-center justify-center gap-2 text-sm font-bold transition-colors"
                                >
                                    {t('search.viewAll', 'View all results')}
                                    <ArrowRight className={clsx("w-4 h-4", isRTL && "rotate-180")} />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="p-8 text-center">
                            <div className="text-gray-400 mb-1">{t('search.noResults', 'No results found')}</div>
                            <div className="text-xs text-gray-400">"{query}"</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
